import React, { useState } from 'react';
import { Box, Button, Typography, Stack, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
interface FormNavProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  isSubmitConfirmation?: boolean;
}
import { useFormContext } from '../context/FormContext';

const FormNav: React.FC<FormNavProps> = ({ currentStep, totalSteps, onNext, onPrevious, isSubmitConfirmation = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { saveDraft } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);

  const handleNext = async () => {
    // Auto-save draft before moving to next step (but not on submit step)
    if (currentStep < totalSteps - 1) {
      setIsSaving(true);
      try {
        await saveDraft(currentStep);
      } catch (error) {
        console.error('Error auto-saving draft:', error);
        // Don't block navigation on save failure
      } finally {
        setIsSaving(false);
      }
    }
    onNext();
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: { xs: 3, sm: 4 },
        pt: { xs: 1.5, sm: 2 },
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        gap: { xs: 2, sm: 0 },
      }}
    >
      <Stack 
        direction={{ xs: 'row', sm: 'row' }} // Change to row on mobile for better usability
        spacing={{ xs: 1, sm: 2 }}
        sx={{ 
          width: { xs: '100%', sm: 'auto' } // Full width on mobile
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={onPrevious}
          disabled={currentStep === 0 || isSubmitConfirmation}
          startIcon={!isMobile && <ArrowBack />}
          sx={{
            backgroundColor: 'transparent !important',
            color: '#ED7420 !important',
            border: '1px solid #ED7420 !important',
            flex: { xs: 1, sm: 'none' }, // Equal width on mobile
            minHeight: { xs: '44px', sm: 'auto' }, // Taller button on mobile
            '&:hover, &:focus': {
              color: '#fff !important',
              backgroundColor: '#ff6a00 !important',
              borderColor: '#ff6a00 !important',
            }
          }}
        >
          {isMobile ? 'Back' : 'Previous'}
        </Button>
        
        {currentStep < totalSteps - 1 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={isSaving}
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : (!isMobile && <ArrowForward />)}
            sx={{
              backgroundColor: '#ED7420 !important',
              color: '#fff !important',
              border: '1px solid #ED7420 !important',
              flex: { xs: 1, sm: 'none' }, // Equal width on mobile
              minHeight: { xs: '44px', sm: 'auto' }, // Taller button on mobile
              '&:hover, &:focus': {
                color: '#ff6a00 !important',
                backgroundColor: '#fff !important',
                borderColor: '#ff6a00 !important',
              }
            }}
          >
            {isSaving ? 'Saving...' : (isMobile ? 'Continue' : 'Next')}
          </Button>
        )}
      </Stack>
      
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ 
          mb: { xs: 0.5, sm: 0 },
          fontSize: { xs: '0.8rem', sm: 'inherit' }
        }}
      >
        Step {currentStep + 1} of {totalSteps}
      </Typography>
    </Box>
  );
};

export default FormNav;